// Card 65, the pool monument (R1248, ui-map-interface.md §17.3.65).
//
// A famous name of ANOTHER world. The meta server keeps one memorial pool
// for the whole fleet, and at a season roll it serves each world a few
// entries of other worlds (R1243, `numbers.md` §14.1). World generation
// keeps one water cell free for each of them, and this piece draws a
// laurel sprig on that cell and writes whose name it is and which sea she
// won.
//
// **THE SPRIG IS THE HALL OF FAME'S SIGN.** The owner picked card 18 of
// mockup round 059 on 2026-09-24: one curved stem and seven leaves. The
// pool IS the Hall of Fame, so the sprig names the source of the monument,
// and no ruin says "a hold stood here", because none did.
//
// **A POOL MONUMENT IS NOT A SITE.** The wire carries no `Site`, the cell
// takes no hold and no installation, and the piece answers no `pick`: the
// R964 rule "a monument answers no pick" stands (R1243).
//
// **THE COST IS ONE CELL.** A map carries two, and the pack of one is
// `stem_steps` segments, seven leaf sprites and two runs. The piece packs
// on a hello, a reading, a zoom band and a font, and costs one flush a
// frame after that.

import { chart as S } from '../../../gen/strings';
import { MonumentKind, type Monument } from '../../../gen/proto/aquaphobia';
import { cellCenter } from '../../../world';
import { chartMarkKey } from '../../gfx/atlas';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { seasonChampion } from './memorial-reef';
import { CHART_INK, faceOf, inkOf } from './shared';

/** One pool monument, as the last pack read it. */
interface Pool {
  x: number;
  y: number;
  /** The winner's name, her origin world and the season she won. */
  commander: string;
  world: string;
  season: number;
}

/** The figures of the stem, as `[chart.piece.pool_monument.fig]` states them. */
export interface StemFig {
  readonly stem_x0: number; readonly stem_y0: number;
  readonly stem_cx: number; readonly stem_cy: number;
  readonly stem_x1: number; readonly stem_y1: number;
}

/** The point at `t` of the quadratic stem, in px from the cell centre. */
export function stemAt(f: StemFig, t: number): [number, number] {
  const a = (1 - t) * (1 - t), b = 2 * (1 - t) * t, c = t * t;
  return [
    a * f.stem_x0 + b * f.stem_cx + c * f.stem_x1,
    a * f.stem_y0 + b * f.stem_cy + c * f.stem_y1,
  ];
}

export class PoolMonumentPiece implements Piece {
  readonly id = 'pool_monument' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The monument stood before this season opened, so a replay film keeps it. */
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'zoomband', 'font'] as const;

  /** The pool monuments of the last pack. The tests read them. */
  pools: Pool[] = [];

  visible(ctx: ChartContext): boolean {
    return ctx.state.monuments.some(isPool);
  }

  rebuild(ctx: ChartContext): void {
    this.pools = [];
    for (const m of ctx.state.monuments) {
      if (!isPool(m) || m.cells.length === 0) continue;
      const [x, y] = cellCenter(m.cells[0]);
      this.pools.push({ x, y, commander: m.commander, world: m.world, season: m.season });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.pool_monument;
    const f = t.fig;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const face = faceOf(ctx.reading);
    const ink = inkOf(r.ink, CHART_INK.pale);
    // The historical leaves are filled in the stone ink, which is the ink
    // of the memorial reef's coral; the modern leaves are stroked thin in
    // the pale ink of the stem.
    const leafInk = hist ? inkOf(r.ink, CHART_INK.ridge) : ink;
    const leaf = ctx.glyphs.mark(chartMarkKey(hist ? 'leaf-fill' : 'leaf-line'));
    const steps = Math.max(1, Math.round(f.stem_steps));
    for (const p of this.pools) {
      let [ax, ay] = stemAt(f, 0);
      for (let i = 1; i <= steps; i++) {
        const [bx, by] = stemAt(f, i / steps);
        sinks.segments.line(
          p.x + ax, p.y + ay, p.x + bx, p.y + by,
          r.width, ink, f.stem_alpha * r.alpha,
        );
        ax = bx; ay = by;
      }
      for (let k = 0; k < f.leaves; k++) {
        const [lx, ly] = stemAt(f, f.leaf_t0 + k * f.leaf_dt);
        const side = k % 2 ? 1 : -1;
        const rot = f.leaf_turn + side * f.leaf_spread;
        // The card turns the pen by `rot` and draws the leaf `leaf_off` px
        // down its own short axis, which is this offset in world px.
        const off = side * f.leaf_off;
        sinks.quads.sprite(
          p.x + lx - Math.sin(rot) * off, p.y + ly + Math.cos(rot) * off,
          rot, leaf, leafInk, f.leaf_alpha * r.alpha, 1, 0, false,
        );
      }
      const head = p.y + f.label_dy;
      // The two lines are one block, so they grow about one pivot.
      const pivot = { x: p.x, y: head };
      ctx.glyphs.word(
        sinks.quads, poolTitle(p, hist),
        p.x, head, face, t.text.main, ink, r.alpha, { anchor: 'centre', pivot },
      );
      // The champion line of a pool monument never names THIS world, so it
      // takes the plain form and not `champion()` of the memorial reef.
      ctx.glyphs.word(
        sinks.quads, seasonChampion(p.season, hist),
        p.x, head + r.fig.line_h,
        face, t.text.sub, CHART_INK.dim, r.alpha, { anchor: 'centre', pivot },
      );
    }
  }
}

/** The first line: the winner and her origin world, or her name alone. */
function poolTitle(p: Pool, hist: boolean): string {
  if (p.world === '') return p.commander;
  return hist
    ? S.pool_monument_historical({ name: p.commander, world: p.world })
    : S.pool_monument_modern({ name: p.commander, world: p.world });
}

/** Is this monument a pool monument? */
function isPool(m: Monument): boolean {
  return m.kind === MonumentKind.MONUMENT_KIND_POOL;
}

/** The one instance the registry holds. */
export const poolMonument = new PoolMonumentPiece();
